import {
   disableLogTagInStorage,
   enableLogTagInStorage,
   getEnabledLogTagsFromStorage,
   isLogTagEnabledInStorage
} from "./utils/storage/loggerStorageUtils";

export const logTags = [
   "account",
   "auth",
   "cart",
   "categories",
   "claims",
   "contentful",
   "delivery",
   "favorites",
   "logger",
   "login-state",
   "mocks",
   "order",
   "products",
   "promotions",
   "recipients",
   "recommendations",
   "search",
   "scanner",
   "subscription",
   "tracking",
   "ui",
   "utils",
   "websockets"
] as const;

export type LogTag = (typeof logTags)[number];

type LogLevel = "log" | "warn" | "error";

let enabledTags = new Set<string>(getEnabledLogTagsFromStorage());

const refreshTagsFromStorage = () => {
   enabledTags = new Set(getEnabledLogTagsFromStorage());
};

const shouldLog = (tag: LogTag, level: LogLevel): boolean => {
   if (level === "warn" || level === "error") return true;
   return enabledTags.has(tag);
};

const logInternal = (level: LogLevel, tag: LogTag, args: unknown[]) => {
   if (!shouldLog(tag, level)) return;

   const prefix = `[${tag}]`;

   // biome-ignore lint/suspicious/noConsole: This is the only place where console logging is allowed
   console[level](prefix, ...args);
};

export const logger = {
   log: (tag: LogTag, ...args: unknown[]) => logInternal("log", tag, args),
   warn: (tag: LogTag, ...args: unknown[]) => logInternal("warn", tag, args),
   error: (tag: LogTag, ...args: unknown[]) => logInternal("error", tag, args),

   enable: (tag: LogTag) => {
      enableLogTagInStorage(tag);
      refreshTagsFromStorage();
   },

   disable: (tag: LogTag) => {
      disableLogTagInStorage(tag);
      refreshTagsFromStorage();
   },

   isEnabled: (tag: LogTag) => isLogTagEnabledInStorage(tag),

   getEnabledTags: () => Array.from(enabledTags),

   enableAllTags: () => {
      for (const tag of logTags) {
         enableLogTagInStorage(tag);
      }
      refreshTagsFromStorage();
   },

   disableAllTags: () => {
      for (const tag of logTags) {
         disableLogTagInStorage(tag);
      }
      refreshTagsFromStorage();
   }
};
